/**
 * NOTE (Yuxin, 2026-01-31): useSandboxFiles hook
 *
 * Fetches the list of page files from the sandbox filesystem.
 * Uses the agent connection from useReploAgent to avoid multiple connections.
 */

import type { ReploAgent } from "@agent-ui/features/chat/hooks/useReploAgentConnection";

import { useQuery, useQueryClient } from "@tanstack/react-query";

const LIST_FILES_TIMEOUT_MS = 10_000;

// =============================================================================
// Hook
// =============================================================================

export function useSandboxFiles({
  agent,
  projectId,
  sessionUuid,
}: {
  agent: ReploAgent;
  projectId: string | null;
  sessionUuid?: string | null;
}) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["sandboxFiles", projectId, sessionUuid],
    queryFn: async () => {
      if (!projectId) {
        // eslint-disable-next-line replo/no-raw-error-throw
        throw new Error("No project ID");
      }

      const result = await Promise.race([
        agent.call("listFiles", [
          { sessionUuid: sessionUuid ?? undefined },
        ]) as Promise<{
          files: {
            route: string;
            filepath: string;
            modifiedAt?: number;
            createdAt?: number;
          }[];
        }>,
        new Promise<never>((_resolve, reject) => {
          setTimeout(
            () => reject(new Error("listFiles timed out")),
            LIST_FILES_TIMEOUT_MS,
          );
        }),
      ]);
      return result.files;
    },
    enabled: Boolean(projectId),
    staleTime: 10_000,
    refetchInterval: () => {
      if (!sessionUuid) {
        return false;
      }
      return 5_000;
    },
    retry: 3,
    retryDelay: (attempt) => Math.min(2_000 * 2 ** attempt, 15_000),
    refetchOnWindowFocus: true,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({
      queryKey: ["sandboxFiles", projectId],
    });
  };

  return {
    files: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    invalidate,
  };
}
